import React, { useEffect, useState } from 'react';
import BrigadeForm from './BrigadeForm';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import '../../css/brigade-list.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiService from '../../shared/services/ApiService';

const STATUS_CONFIG = {
    'Pendiente': { color: '#95a5a6', bg: '#f0f0f0', label: 'Pendiente' },
    'Asignada': { color: '#3498db', bg: '#ebf5fb', label: 'Asignada' },
    'En proceso': { color: '#f39c12', bg: '#fef9e7', label: 'En proceso' },
    'Pausada': { color: '#9b59b6', bg: '#f4ecf7', label: 'Pausada' },
    'Finalizada': { color: '#27ae60', bg: '#eafaf1', label: 'Finalizada' },
    'Cancelada': { color: '#e74c3c', bg: '#fdedec', label: 'Cancelada' },
};

const BrigadeList = () => {
    const [brigades, setBrigades] = useState([]);
    const [selectedBrigade, setSelectedBrigade] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedBrigadeId, setExpandedBrigadeId] = useState(null);
    const [historyBrigadeId, setHistoryBrigadeId] = useState(null);
    const [brigadeHistory, setBrigadeHistory] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');

    // Pagination and filters
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(9);
    const [searchTerm, setSearchTerm] = useState('');
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [sortBy, setSortBy] = useState('name'); // 'name', 'members'
    const [filterMembers, setFilterMembers] = useState('all'); // 'all', 'with', 'without'

    useEffect(() => {
        fetchBrigades();
    }, []);

    const toggleExpandBrigade = (id) => {
        setExpandedBrigadeId(prevId => (prevId === id ? null : id));
    };

    const fetchBrigades = async () => {
        setLoading(true);
        try {
            const response = await apiService.get(`/api/brigadas`);
            setBrigades(response.data);
            setError(null);
        } catch (err) {
            setError('Error al cargar las brigadas. Por favor, intente de nuevo.');
            toast.error('Error al cargar las brigadas');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await apiService.delete(`/api/brigadas/${id}`);
            toast.success("Brigada eliminada con éxito");
            fetchBrigades();
        } catch (err) {
            toast.error("Error al eliminar la brigada");
        }
    };

    const handleEdit = (brigade) => {
        setSelectedBrigade(brigade);
        setIsFormVisible(true);
        setTimeout(() => {
            const formElement = document.querySelector('.brigade-form-wrapper');
            if (formElement) {
                formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    const handleSubmit = async (brigade) => {
        try {
            if (selectedBrigade) {
                await apiService.put(`/api/brigadas/${selectedBrigade._id}`, brigade);
                toast.success("Brigada actualizada con éxito");
            } else {
                await apiService.post(`/api/brigadas`, brigade);
                toast.success("Brigada añadida con éxito");
            }
            setSelectedBrigade(null);
            setIsFormVisible(false);
            fetchBrigades();
        } catch (err) {
            toast.error(selectedBrigade ? "Error al actualizar la brigada" : "Error al crear la brigada");
        }
    };

    const handleCancelForm = () => {
        setSelectedBrigade(null);
        setIsFormVisible(false);
    };

    const handleStatusChange = async (brigadeId, newStatus) => {
        const comment = window.prompt(`Comentario para cambio a "${newStatus}" (opcional):`);
        if (comment === null) return; // cancelled
        try {
            await apiService.put(`/api/brigadas/${brigadeId}/status`, { newStatus, comment });
            toast.success(`Estado cambiado a "${newStatus}"`);
            fetchBrigades();
        } catch (err) {
            const msg = err.response?.data?.message || 'Error al cambiar estado';
            toast.error(msg);
        }
    };

    const toggleHistory = async (brigadeId) => {
        if (historyBrigadeId === brigadeId) {
            setHistoryBrigadeId(null);
            setBrigadeHistory([]);
            return;
        }
        try {
            const response = await apiService.get(`/api/brigadas/${brigadeId}/history`);
            setBrigadeHistory(response.data.history || []);
            setHistoryBrigadeId(brigadeId);
        } catch (err) {
            toast.error('Error al cargar historial');
        }
    };

    const getHistoryTypeLabel = (type) => {
        const labels = {
            'status_change': 'Cambio de estado',
            'member_added': 'Miembro agregado',
            'member_removed': 'Miembro removido',
            'completion': 'Completado',
            'created': 'Creada'
        };
        return labels[type] || type;
    };

    // Generar PDF individual
    const generatePDFForBrigade = (brigade) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        
        // Header
        doc.setFillColor(0, 87, 166);
        doc.rect(0, 0, pageWidth, 40, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text('Reporte de Brigada', pageWidth / 2, 25, { align: 'center' });
        
        // Brigada info
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(`Brigada: ${brigade.name}`, 15, 55);
        
        if (brigade.description) {
            doc.setFontSize(12);
            doc.setFont('helvetica', 'normal');
            doc.text(`Descripción: ${brigade.description}`, 15, 65);
        }

        let yPosition = brigade.description ? 80 : 70;
        
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Miembros:', 15, yPosition);
        yPosition += 10;

        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        
        if (brigade.members && brigade.members.length > 0) {
            brigade.members.forEach((member, index) => {
                if (yPosition > 270) {
                    doc.addPage();
                    yPosition = 20;
                }
                doc.setFillColor(52, 152, 219);
                doc.circle(20, yPosition - 2, 2, 'F');
                doc.text(`${member.username || member.name}`, 25, yPosition);
                yPosition += 8;
            });
        } else {
            doc.text('No hay miembros en esta brigada', 20, yPosition);
        }

        doc.save(`${brigade.name}_report.pdf`);
        toast.success(`Reporte PDF generado: ${brigade.name}`);
    };

    // Generar Excel
    const generateExcel = () => {
        const ws = XLSX.utils.json_to_sheet(brigades.map(brigade => ({
            Nombre: brigade.name,
            Descripción: brigade.description || 'N/A',
            'Cantidad de Miembros': brigade.members ? brigade.members.length : 0,
            Miembros: brigade.members ? brigade.members.map(m => m.username).join(', ') : 'N/A',
        })));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Brigadas');
        XLSX.writeFile(wb, `brigadas_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success("Reporte Excel generado");
    };

    // Generar CSV
    const generateCSV = () => {
        const csvData = brigades.map(brigade => ({
            Nombre: brigade.name,
            Descripción: brigade.description || 'N/A',
            'Cantidad de Miembros': brigade.members ? brigade.members.length : 0,
            Miembros: brigade.members ? brigade.members.map(m => m.username).join(', ') : 'N/A',
        }));

        const csvRows = [
            ['Nombre', 'Descripción', 'Cantidad de Miembros', 'Miembros'],
            ...csvData.map(brigade => [
                brigade.Nombre,
                brigade.Descripción,
                brigade['Cantidad de Miembros'],
                brigade.Miembros
            ]),
        ];

        const csvString = csvRows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('href', url);
        a.setAttribute('download', `brigadas_${new Date().toISOString().split('T')[0]}.csv`);
        a.click();
        toast.success("Reporte CSV generado");
    };

    // Filtrado y ordenamiento
    let filteredBrigades = brigades.filter(brigade => {
        const matchesSearch = brigade.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (brigade.description && brigade.description.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesFilter = filterMembers === 'all' ||
                            (filterMembers === 'with' && brigade.members && brigade.members.length > 0) ||
                            (filterMembers === 'without' && (!brigade.members || brigade.members.length === 0));

        const matchesStatus = statusFilter === 'all' || brigade.status === statusFilter;

        return matchesSearch && matchesFilter && matchesStatus;
    });

    // Ordenamiento
    filteredBrigades = [...filteredBrigades].sort((a, b) => {
        if (sortBy === 'name') {
            return a.name.localeCompare(b.name);
        } else if (sortBy === 'members') {
            const aMembers = a.members ? a.members.length : 0;
            const bMembers = b.members ? b.members.length : 0;
            return bMembers - aMembers;
        }
        return 0;
    });

    // Estadísticas
    const statistics = {
        total: brigades.length,
        withMembers: brigades.filter(b => b.members && b.members.length > 0).length,
        withoutMembers: brigades.filter(b => !b.members || b.members.length === 0).length,
        totalMembers: brigades.reduce((sum, b) => sum + (b.members ? b.members.length : 0), 0),
        byStatus: Object.keys(STATUS_CONFIG).reduce((acc, s) => {
            acc[s] = brigades.filter(b => (b.status || 'Pendiente') === s).length;
            return acc;
        }, {})
    };

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentBrigades = filteredBrigades.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredBrigades.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <div className="brigade-container-modern">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

            {/* Header */}
            <div className="brigade-header-modern">
                <div className="header-content">
                    <div className="header-title-section">
                        <div className="title-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                        </div>
                        <div>
                            <h1 className="page-title">Gestión de Brigadas</h1>
                            <p className="page-subtitle">Administra y organiza tus brigadas de trabajo</p>
                        </div>
                    </div>
                    <div className="header-actions">
                        <button
                            className={`btn-primary ${isFormVisible ? 'btn-danger' : ''}`}
                            onClick={() => {
                                setIsFormVisible(!isFormVisible);
                                if (isFormVisible) {
                                    setSelectedBrigade(null);
                                }
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                {isFormVisible ? (
                                    <>
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </>
                                ) : (
                                    <>
                                        <line x1="12" y1="5" x2="12" y2="19"/>
                                        <line x1="5" y1="12" x2="19" y2="12"/>
                                    </>
                                )}
                            </svg>
                            {isFormVisible ? 'Cancelar' : 'Nueva Brigada'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Estadísticas */}
            <div className="stats-section">
                <div className="stat-card">
                    <div className="stat-icon total">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                    </div>
                    <div className="stat-content">
                        <h3>{statistics.total}</h3>
                        <p>Total Brigadas</p>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon active">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                    </div>
                    <div className="stat-content">
                        <h3>{statistics.withMembers}</h3>
                        <p>Con Miembros</p>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon inactive">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <line x1="23" y1="11" x2="17" y2="11"/>
                        </svg>
                    </div>
                    <div className="stat-content">
                        <h3>{statistics.withoutMembers}</h3>
                        <p>Sin Miembros</p>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon members">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                    </div>
                    <div className="stat-content">
                        <h3>{statistics.totalMembers}</h3>
                        <p>Total Miembros</p>
                    </div>
                </div>
            </div>

            {/* Status filter tabs */}
            <div className="status-tabs-brigade">
                <button
                    className={`status-tab-btn ${statusFilter === 'all' ? 'active' : ''}`}
                    onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
                >
                    Todas ({brigades.length})
                </button>
                {Object.entries(STATUS_CONFIG).map(([status, cfg]) => (
                    <button
                        key={status}
                        className={`status-tab-btn ${statusFilter === status ? 'active' : ''}`}
                        onClick={() => { setStatusFilter(status); setCurrentPage(1); }}
                        style={statusFilter === status ? { borderColor: cfg.color, color: cfg.color } : {}}
                    >
                        <span className="status-dot" style={{ background: cfg.color }}></span>
                        {cfg.label} ({statistics.byStatus[status] || 0})
                    </button>
                ))}
            </div>

            {/* Formulario */}
            {isFormVisible && (
                <div className="brigade-form-wrapper">
                    <BrigadeForm
                        onSubmit={handleSubmit}
                        brigade={selectedBrigade}
                        onCancel={handleCancelForm}
                    />
                </div>
            )}

            {/* Sección de lista */}
            <div className="brigade-list-section">
                {/* Controles */}
                <div className="list-controls-modern">
                    <div className="controls-left">
                        <div className="search-box-modern">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"/>
                                <path d="m21 21-4.35-4.35"/>
                            </svg>
                            <input
                                type="text"
                                placeholder="Buscar brigadas..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            {searchTerm && (
                                <button 
                                    className="clear-search-btn"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            )}
                        </div>

                        <div className="filter-group">
                            <select
                                className="filter-select"
                                value={filterMembers}
                                onChange={(e) => {
                                    setFilterMembers(e.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="all">Todas las brigadas</option>
                                <option value="with">Con miembros</option>
                                <option value="without">Sin miembros</option>
                            </select>

                            <select
                                className="filter-select"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="name">Ordenar por nombre</option>
                                <option value="members">Ordenar por miembros</option>
                            </select>
                        </div>
                    </div>

                    <div className="controls-right">
                        <div className="view-toggles">
                            <button
                                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                                onClick={() => setViewMode('grid')}
                                title="Vista de cuadrícula"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="7" height="7"/>
                                    <rect x="14" y="3" width="7" height="7"/>
                                    <rect x="14" y="14" width="7" height="7"/>
                                    <rect x="3" y="14" width="7" height="7"/>
                                </svg>
                            </button>
                            <button
                                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                                onClick={() => setViewMode('list')}
                                title="Vista de lista"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="8" y1="6" x2="21" y2="6"/>
                                    <line x1="8" y1="12" x2="21" y2="12"/>
                                    <line x1="8" y1="18" x2="21" y2="18"/>
                                    <line x1="3" y1="6" x2="3.01" y2="6"/>
                                    <line x1="3" y1="12" x2="3.01" y2="12"/>
                                    <line x1="3" y1="18" x2="3.01" y2="18"/>
                                </svg>
                            </button>
                        </div>

                        <div className="export-buttons-modern">
                            <button className="btn-export excel" onClick={generateExcel} title="Exportar a Excel">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                    <line x1="16" y1="13" x2="8" y2="13"/>
                                    <line x1="16" y1="17" x2="8" y2="17"/>
                                    <polyline points="10 9 9 9 8 9"/>
                                </svg>
                                Excel
                            </button>
                            <button className="btn-export csv" onClick={generateCSV} title="Exportar a CSV">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                    <line x1="16" y1="13" x2="8" y2="13"/>
                                    <line x1="16" y1="17" x2="8" y2="17"/>
                                </svg>
                                CSV
                            </button>
                        </div>
                    </div>
                </div>

                {/* Contenido */}
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner-modern"></div>
                        <p>Cargando brigadas...</p>
                    </div>
                ) : error ? (
                    <div className="error-state">
                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        <h3>Error al cargar brigadas</h3>
                        <p>{error}</p>
                        <button className="btn-retry" onClick={fetchBrigades}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="23 4 23 10 17 10"/>
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                            </svg>
                            Reintentar
                        </button>
                    </div>
                ) : (
                    <>
                        {currentBrigades.length > 0 ? (
                            <div className={`brigades-${viewMode}`}>
                                {currentBrigades.map((brigade) => (
                                    <div key={brigade._id} className="brigade-card-modern">
                                        <div className="card-header-modern">
                                            <div className="brigade-avatar" style={{ background: STATUS_CONFIG[brigade.status || 'Pendiente']?.color || '#95a5a6' }}>
                                                {brigade.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="brigade-info">
                                                <h3 className="brigade-name-modern">{brigade.name}</h3>
                                                {brigade.description && (
                                                    <p className="brigade-description-modern">{brigade.description}</p>
                                                )}
                                                <span
                                                    className="brigade-status-badge"
                                                    style={{
                                                        color: STATUS_CONFIG[brigade.status || 'Pendiente']?.color,
                                                        background: STATUS_CONFIG[brigade.status || 'Pendiente']?.bg
                                                    }}
                                                >
                                                    {STATUS_CONFIG[brigade.status || 'Pendiente']?.label}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Status change */}
                                        <div className="brigade-status-actions">
                                            <select
                                                className="status-select-brigade"
                                                value=""
                                                onChange={(e) => {
                                                    if (e.target.value) handleStatusChange(brigade._id, e.target.value);
                                                }}
                                            >
                                                <option value="">Cambiar estado...</option>
                                                {['Pendiente', 'Asignada', 'En proceso', 'Pausada', 'Finalizada', 'Cancelada']
                                                    .filter(s => s !== (brigade.status || 'Pendiente'))
                                                    .map(s => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))
                                                }
                                            </select>
                                            <button
                                                className="history-toggle-btn"
                                                onClick={() => toggleHistory(brigade._id)}
                                                title="Ver historial"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"/>
                                                    <polyline points="12 6 12 12 16 14"/>
                                                </svg>
                                                {historyBrigadeId === brigade._id ? 'Ocultar' : 'Historial'}
                                            </button>
                                        </div>

                                        {/* Timeline */}
                                        {historyBrigadeId === brigade._id && brigadeHistory.length > 0 && (
                                            <div className="brigade-timeline">
                                                {brigadeHistory.slice(0, 10).map((entry, idx) => (
                                                    <div key={entry._id || idx} className="timeline-item">
                                                        <div className="timeline-dot" style={{
                                                            background: entry.newStatus ? (STATUS_CONFIG[entry.newStatus]?.color || '#95a5a6') : '#3498db'
                                                        }}></div>
                                                        <div className="timeline-content">
                                                            <span className="timeline-type">{getHistoryTypeLabel(entry.type)}</span>
                                                            {entry.previousStatus && entry.newStatus && (
                                                                <span className="timeline-change">
                                                                    {entry.previousStatus} → {entry.newStatus}
                                                                </span>
                                                            )}
                                                            {entry.comment && <p className="timeline-comment">{entry.comment}</p>}
                                                            <span className="timeline-date">
                                                                {new Date(entry.date).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                                                                {entry.changedBy?.username && ` — ${entry.changedBy.username}`}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="card-body-modern">
                                            <div className="members-section">
                                                <div className="members-header">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                                        <circle cx="9" cy="7" r="4"/>
                                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                                    </svg>
                                                    <span className="members-count-badge">
                                                        {brigade.members ? brigade.members.length : 0} miembro{brigade.members && brigade.members.length !== 1 ? 's' : ''}
                                                    </span>
                                                </div>

                                                {brigade.members && brigade.members.length > 0 && (
                                                    <button
                                                        className="expand-btn"
                                                        onClick={() => toggleExpandBrigade(brigade._id)}
                                                    >
                                                        {expandedBrigadeId === brigade._id ? 'Ocultar' : 'Ver'} miembros
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{
                                                            transform: expandedBrigadeId === brigade._id ? 'rotate(180deg)' : 'rotate(0deg)',
                                                            transition: 'transform 0.3s ease'
                                                        }}>
                                                            <polyline points="6 9 12 15 18 9"/>
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>

                                            {expandedBrigadeId === brigade._id && brigade.members && brigade.members.length > 0 && (
                                                <div className="members-list-expanded">
                                                    {brigade.members.map((member, index) => (
                                                        <div key={index} className="member-item">
                                                            <div className="member-avatar-small">
                                                                {member.username.charAt(0).toUpperCase()}
                                                            </div>
                                                            <span>{member.username || member.name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className="card-actions-modern">
                                            <button
                                                className="action-btn-modern edit"
                                                onClick={() => handleEdit(brigade)}
                                                title="Editar brigada"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                </svg>
                                            </button>
                                            <button
                                                className="action-btn-modern pdf"
                                                onClick={() => generatePDFForBrigade(brigade)}
                                                title="Generar PDF"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                                    <polyline points="14 2 14 8 20 8"/>
                                                    <line x1="16" y1="13" x2="8" y2="13"/>
                                                    <line x1="16" y1="17" x2="8" y2="17"/>
                                                    <polyline points="10 9 9 9 8 9"/>
                                                </svg>
                                            </button>
                                            <button
                                                className="action-btn-modern delete"
                                                onClick={() => {
                                                    if (window.confirm(`¿Está seguro que desea eliminar la brigada "${brigade.name}"?`)) {
                                                        handleDelete(brigade._id);
                                                    }
                                                }}
                                                title="Eliminar brigada"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6"/>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                                    <line x1="10" y1="11" x2="10" y2="17"/>
                                                    <line x1="14" y1="11" x2="14" y2="17"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state-modern">
                                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                    <circle cx="9" cy="7" r="4"/>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                </svg>
                                <h3>No hay brigadas</h3>
                                <p>
                                    {searchTerm
                                        ? `No se encontraron brigadas que coincidan con "${searchTerm}"`
                                        : filterMembers === 'with'
                                        ? 'No hay brigadas con miembros'
                                        : filterMembers === 'without'
                                        ? 'Todas las brigadas tienen miembros'
                                        : 'No hay brigadas registradas. Crea una nueva para comenzar.'}
                                </p>
                                {(searchTerm || filterMembers !== 'all') && (
                                    <button
                                        className="btn-clear-filters"
                                        onClick={() => {
                                            setSearchTerm('');
                                            setFilterMembers('all');
                                        }}
                                    >
                                        Limpiar filtros
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Paginación */}
                        {totalPages > 1 && (
                            <div className="pagination-modern">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="page-btn-modern"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="15 18 9 12 15 6"/>
                                    </svg>
                                </button>

                                <div className="page-numbers-modern">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(num =>
                                            num === 1 ||
                                            num === totalPages ||
                                            (num >= currentPage - 1 && num <= currentPage + 1)
                                        )
                                        .map((number, index, array) => {
                                            if (index > 0 && array[index - 1] !== number - 1) {
                                                return (
                                                    <React.Fragment key={`ellipsis-${number}`}>
                                                        <span className="ellipsis-modern">...</span>
                                                        <button
                                                            onClick={() => paginate(number)}
                                                            className={`page-btn-modern ${currentPage === number ? 'active' : ''}`}
                                                        >
                                                            {number}
                                                        </button>
                                                    </React.Fragment>
                                                );
                                            }
                                            return (
                                                <button
                                                    key={number}
                                                    onClick={() => paginate(number)}
                                                    className={`page-btn-modern ${currentPage === number ? 'active' : ''}`}
                                                >
                                                    {number}
                                                </button>
                                            );
                                        })
                                    }
                                </div>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="page-btn-modern"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="9 18 15 12 9 6"/>
                                    </svg>
                                </button>
                            </div>
                        )}

                        <div className="pagination-info-modern">
                            Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredBrigades.length)} de {filteredBrigades.length} brigada{filteredBrigades.length !== 1 ? 's' : ''}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default BrigadeList;